// 通知路由 — station role 分流共用邏輯（前端 only）
// 預設規則 + DB 覆蓋，決定「某訂單進到某 status 時，本機裝置要不要閃 / 響」
// see architecture.md#L510

/** 站點角色 */
export const STATION_ROLES = Object.freeze(['counter', 'kitchen', 'expo', 'all']);

/** 各 role 的顯示名稱（zh-TW） */
export const STATION_LABELS = Object.freeze({
  counter: '櫃檯',
  kitchen: '後廚',
  expo: '外場出餐',
  all: '全通知（不分流）',
});

/** 訂單狀態顯示名稱 */
export const STATUS_LABELS_ZH = Object.freeze({
  pending:   '新訂單待收款',
  paid:      '付款完成待準備',
  preparing: '準備中',
  ready:     '完成待出餐',
  completed: '已完成',
  cancelled: '已取消',
  refunded:  '已退款',
});

/** Hardcoded 預設分流（DB 沒覆蓋時用這個） */
export const DEFAULT_ROUTES = Object.freeze({
  pending:   { notify_roles: ['counter'],  sound: true,  flash: true  },
  paid:      { notify_roles: ['kitchen'],  sound: true,  flash: true  },
  preparing: { notify_roles: [],           sound: false, flash: false },
  ready:     { notify_roles: ['expo'],     sound: true,  flash: true  },
  completed: { notify_roles: ['counter'],  sound: false, flash: false },
  cancelled: { notify_roles: ['counter'],  sound: false, flash: false },
  refunded:  { notify_roles: ['counter'],  sound: false, flash: false },
});

const STATION_ROLE_KEY = 'pos-station-role';
const STATION_NAME_KEY = 'pos-station-name';

/** 讀本機 station role（預設 'all' — 不分流） */
export function getStationRole() {
  try {
    const v = localStorage.getItem(STATION_ROLE_KEY);
    if (v && STATION_ROLES.includes(v)) return v;
  } catch { /* ssr / privacy mode */ }
  return 'all';
}

export function setStationRole(role) {
  if (!STATION_ROLES.includes(role)) throw new Error(`invalid role: ${role}`);
  try { localStorage.setItem(STATION_ROLE_KEY, role); } catch { /* noop */ }
}

export function getStationName() {
  try { return localStorage.getItem(STATION_NAME_KEY) || ''; } catch { return ''; }
}

export function setStationName(name) {
  try { localStorage.setItem(STATION_NAME_KEY, String(name || '').slice(0, 40)); } catch { /* noop */ }
}

/**
 * 給一個 status，回傳 { notify_roles, sound, flash }
 * 優先序：DB rules > DEFAULT_ROUTES
 * @param {string} toStatus
 * @param {Array<{to_status:string, notify_roles:string[], sound:boolean, flash:boolean}>} dbRoutes
 */
export function resolveRoute(toStatus, dbRoutes = []) {
  const override = dbRoutes.find(r => r.to_status === toStatus);
  if (override) {
    return {
      notify_roles: override.notify_roles || [],
      sound: override.sound !== false,
      flash: override.flash !== false,
    };
  }
  return DEFAULT_ROUTES[toStatus] || { notify_roles: [], sound: false, flash: false };
}

/**
 * 判斷本機裝置是否該收到某 status 的通知。
 * - 本機 role='all'：所有有 notify_roles 的狀態都會響
 * - DB rule 有 'all' sentinel：視為廣播，所有裝置都響（給整店全員通知用）
 * - 其他情況：只有 notify_roles 包含此 role 才響
 */
export function shouldNotify(toStatus, dbRoutes = [], stationRole = getStationRole()) {
  const { notify_roles, sound, flash } = resolveRoute(toStatus, dbRoutes);
  if (notify_roles.length === 0) return { notify: false, sound: false, flash: false };
  const notify =
    stationRole === 'all' ||
    notify_roles.includes('all') ||
    notify_roles.includes(stationRole);
  return { notify, sound: notify && sound, flash: notify && flash };
}
